export type Locale = 'id' | 'en';
export const en = {
  loading: 'Loading support chat…',
  unavailable:
    'Chat unavailable. The agent may be inactive or this website is not allowed. Please reload or contact the demo owner.',
  chat: 'support chat',
  close: 'Close support chat',
  open: 'Open support chat',
  placeholder: 'Type your message…',
  message: 'Message',
  send: 'Send',
  contact: 'Share contact details',
  handoff: 'Request a person',
  preparing: 'is preparing a response',
  sources: 'Sources',
  knowledge: 'Knowledge file',
  failed: 'A reply could not be prepared. Please contact the demo owner or request a person.',
  recovering: 'Connection interrupted. Checking your reply automatically…',
  unconfirmed: 'The reply could not be retrieved. Check again without sending another message.',
  checkReply: 'Check reply',
  replyTimeout: 'The reply took too long. Please request a person or contact the demo owner.',
  replySources: 'A sourced answer could not be verified. Please request a person.',
  rateLimited: 'Too many requests. Please wait a minute before sending another message.',
  quotaReached: 'Daily search limit reached. Try after midnight UTC or contact the demo owner.',
  retry: 'Retry',
  details: 'Contact details',
  closeDetails: 'Close contact details',
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  consent: 'I consent to these details being stored so a support agent can contact me.',
  cancel: 'Cancel',
  submit: 'Submit details',
  contactFirst: 'Send a message before sharing details.',
  saved: 'Thanks—your details were saved with consent.',
  invalidContact: 'Could not save details. Check at least one contact field.',
  contactFailed: 'Could not save details. Please retry.',
  handoffFirst: 'Send a message before requesting a person.',
  handoffSaved: 'A human-support request has been created.',
  handoffFailed: 'Could not request a person. Please retry.',
  language: 'Language',
  title: 'Try the company’s AI assistant',
  websiteCallout: 'This is how 24talk could look on your website.',
  chatCallout: 'Try the chat right here.',
  integrationPreviewAlt:
    'Illustration of a storefront website with the 24talk customer support chat open and ready to answer.',
  disclaimer:
    'This is an AI demonstration, not a live transaction service. Answers may be incorrect; confirm important information with the company.',
  privacy:
    'Messages are stored for review by the demo owner and processed by the AI provider. Use fictional data. Do not enter passwords, payment information, or sensitive personal data.',
  human:
    'Human-support requests are recorded for follow-up; they do not connect you directly to an operator.',
};
export type TranslationKey = keyof typeof en;
export const dictionaries: Record<Locale, Record<TranslationKey, string>> = {
  en,
  id: {
    loading: 'Memuat chat bantuan…',
    unavailable:
      'Chat tidak tersedia. Agent mungkin tidak aktif atau situs ini belum diizinkan. Muat ulang atau hubungi pemilik demo.',
    chat: 'chat bantuan',
    close: 'Tutup chat bantuan',
    open: 'Buka chat bantuan',
    placeholder: 'Ketik pesan Anda…',
    message: 'Pesan',
    send: 'Kirim',
    contact: 'Bagikan detail kontak',
    handoff: 'Minta bantuan manusia',
    preparing: 'sedang menyiapkan jawaban',
    sources: 'Sumber',
    knowledge: 'File pengetahuan',
    failed: 'Jawaban belum dapat disiapkan. Hubungi pemilik demo atau minta bantuan manusia.',
    recovering: 'Koneksi terputus. Memeriksa jawaban Anda secara otomatis…',
    unconfirmed: 'Jawaban belum berhasil diambil. Periksa lagi tanpa mengirim pesan baru.',
    checkReply: 'Periksa jawaban',
    replyTimeout:
      'Jawaban membutuhkan waktu terlalu lama. Minta bantuan manusia atau hubungi pemilik demo.',
    replySources:
      'Jawaban dengan sumber yang sesuai belum dapat diverifikasi. Minta bantuan manusia.',
    rateLimited: 'Terlalu banyak permintaan. Tunggu satu menit sebelum mengirim pesan berikutnya.',
    quotaReached:
      'Batas pencarian harian tercapai. Coba setelah tengah malam UTC atau hubungi pemilik demo.',
    retry: 'Coba lagi',
    details: 'Detail kontak',
    closeDetails: 'Tutup detail kontak',
    name: 'Nama',
    email: 'Email',
    phone: 'Telepon',
    consent: 'Saya menyetujui penyimpanan detail ini agar petugas bantuan dapat menghubungi saya.',
    cancel: 'Batal',
    submit: 'Kirim detail',
    contactFirst: 'Kirim pesan sebelum membagikan detail kontak.',
    saved: 'Terima kasih—detail Anda telah disimpan dengan persetujuan.',
    invalidContact: 'Detail gagal disimpan. Periksa setidaknya satu kolom kontak.',
    contactFailed: 'Detail gagal disimpan. Silakan coba lagi.',
    handoffFirst: 'Kirim pesan sebelum meminta bantuan manusia.',
    handoffSaved: 'Permintaan bantuan manusia telah dicatat.',
    handoffFailed: 'Permintaan bantuan gagal. Silakan coba lagi.',
    language: 'Bahasa',
    title: 'Coba asisten AI perusahaan',
    websiteCallout: 'Seperti inilah tampilan website Anda dengan 24talk.',
    chatCallout: 'Coba fitur chat langsung di sini.',
    integrationPreviewAlt:
      'Ilustrasi situs toko dengan chat bantuan pelanggan 24talk terbuka dan siap menjawab.',
    disclaimer:
      'Ini adalah demonstrasi AI, bukan layanan transaksi langsung. Jawaban dapat keliru; konfirmasikan informasi penting kepada perusahaan.',
    privacy:
      'Pesan disimpan untuk ditinjau pemilik demo dan diproses oleh penyedia AI. Gunakan data fiktif. Jangan masukkan password, informasi pembayaran, atau data pribadi sensitif.',
    human:
      'Permintaan bantuan manusia hanya dicatat untuk tindak lanjut; tidak langsung menghubungkan operator.',
  },
};

export const isLocale = (value: unknown): value is Locale => value === 'id' || value === 'en';
export function resolveLocale(input: {
  saved?: string | null;
  requested?: string | null;
  languages?: readonly string[];
  fallback?: string;
}): Locale {
  if (isLocale(input.saved)) return input.saved;
  if (isLocale(input.requested)) return input.requested;
  for (const language of input.languages ?? []) {
    const base = language.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return isLocale(input.fallback) ? input.fallback : 'en';
}
export function browserLocale(agentId: string, fallback = 'en'): Locale {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(`24talk:locale:${agentId}`);
  } catch {
    /* Storage can be blocked in embeds. */
  }
  return resolveLocale({
    saved,
    requested: new URLSearchParams(location.search).get('lang'),
    languages: navigator.languages,
    fallback,
  });
}
export function saveLocale(agentId: string, locale: Locale): void {
  try {
    localStorage.setItem(`24talk:locale:${agentId}`, locale);
  } catch {
    /* Keep the in-memory choice. */
  }
}
